import React, { useState, useRef, useEffect } from 'react';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';
import PreWithLines from '../components/PreWithLines';
import PreWithClickableLines from '../components/PreWithClickableLines';

const JsonPathFinderPage = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [jsonPath, setJsonPath] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [resultPaths, setResultPaths] = useState([]);
  const [highlightedPath, setHighlightedPath] = useState('');
  const [copyPathSuccess, setCopyPathSuccess] = useState(false);
  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const inputLineNumbersRef = useRef(null);
  const outputLineNumbersRef = useRef(null);
  const fileInputRef = useRef(null);

  // Scroll sync (BeautifierPage ile aynı)
  const [isInputScrolling, setIsInputScrolling] = useState(false);
  const [isOutputScrolling, setIsOutputScrolling] = useState(false);

  const handleInputScroll = () => {
    if (inputLineNumbersRef.current && inputRef.current) {
      inputLineNumbersRef.current.scrollTop = inputRef.current.scrollTop;
    }
    if (!isOutputScrolling && outputRef.current && inputRef.current) {
      setIsInputScrolling(true);
      const inputScrollTop = inputRef.current.scrollTop;
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      const scrollRatio = inputScrollTop / (inputScrollHeight - inputClientHeight || 1);
      const outputScrollTop = scrollRatio * (outputScrollHeight - outputClientHeight || 1);
      outputRef.current.scrollTop = outputScrollTop;
      if (outputLineNumbersRef.current) {
        outputLineNumbersRef.current.scrollTop = outputScrollTop;
      }
      setTimeout(() => setIsInputScrolling(false), 50);
    }
  };

  const handleOutputScroll = () => {
    if (outputLineNumbersRef.current && outputRef.current) {
      outputLineNumbersRef.current.scrollTop = outputRef.current.scrollTop;
    }
    if (!isInputScrolling && inputRef.current && outputRef.current) {
      setIsOutputScrolling(true);
      const outputScrollTop = outputRef.current.scrollTop;
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      const scrollRatio = outputScrollTop / (outputScrollHeight - outputClientHeight || 1);
      const inputScrollTop = scrollRatio * (inputScrollHeight - inputClientHeight || 1);
      inputRef.current.scrollTop = inputScrollTop;
      if (inputLineNumbersRef.current) {
        inputLineNumbersRef.current.scrollTop = inputScrollTop;
      }
      setTimeout(() => setIsOutputScrolling(false), 50);
    }
  };

  // Dosya yükleme
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setJsonInput(e.target.result);
      };
      reader.readAsText(file);
    }
  };
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  // Basit bir JSONPath desteği (dot notation, array index)
  const findByPath = (obj, path) => {
    if (!path) return obj;
    const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    let current = obj;
    for (let part of parts) {
      if (part === '') continue;
      if (current && Object.prototype.hasOwnProperty.call(current, part)) {
        current = current[part];
      } else if (Array.isArray(current) && !isNaN(Number(part))) {
        current = current[Number(part)];
      } else {
        return undefined;
      }
    }
    return current;
  };

  // JSON'u satır-path eşleşmesiyle stringify et
  const stringifyWithPaths = (obj) => {
    const lines = [];
    const paths = [];
    const helper = (val, path, indent) => {
      if (typeof val === 'object' && val !== null) {
        if (Array.isArray(val)) {
          lines.push(indent + '[');
          paths.push(path);
          val.forEach((item, idx) => {
            helper(item, path ? `${path}[${idx}]` : `[${idx}]`, indent + '  ');
          });
          lines.push(indent + ']');
          paths.push(path);
        } else {
          lines.push(indent + '{');
          paths.push(path);
          Object.keys(val).forEach((key, i, arr) => {
            const isLast = i === arr.length - 1;
            const keyPath = path ? `${path}.${key}` : key;
            const value = val[key];
            if (typeof value === 'object' && value !== null) {
              lines.push(`${indent}  "${key}":`);
              paths.push(keyPath);
              helper(value, keyPath, indent + '  ');
            } else {
              const valueStr = JSON.stringify(value);
              lines.push(`${indent}  "${key}": ${valueStr}`);
              paths.push(keyPath);
            }
          });
          lines.push(indent + '}');
          paths.push(path);
        }
      } else {
        lines.push(indent + JSON.stringify(val));
        paths.push(path);
      }
    };
    helper(obj, '', '');
    return { lines, paths };
  };

  const handleFindPath = () => {
    setError('');
    setResult('');
    setResultPaths([]);
    setHighlightedPath(jsonPath.trim() ? jsonPath : '');
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data.');
        return;
      }
      const parsed = JSON.parse(jsonInput);
      const value = findByPath(parsed, jsonPath);
      if (typeof value === 'undefined') {
        setError('Path not found.');
        setResult('');
        setResultPaths([]);
      } else {
        if (typeof value === 'object' && value !== null) {
          const { lines, paths } = stringifyWithPaths(value);
          setResult(lines.join('\n'));
          setResultPaths(paths);
        } else {
          setResult(typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value));
          setResultPaths(['']);
        }
      }
    } catch (e) {
      setError('Invalid JSON or path.');
      setResult('');
      setResultPaths([]);
    }
  };

  // pre satırına tıklama
  const handleResultLineClick = (lineIdx) => {
    if (!resultPaths[lineIdx]) return;
    setJsonPath(resultPaths[lineIdx]);
    setHighlightedPath(resultPaths[lineIdx]);
  };

  const handleCopyResult = () => {
    if (!result) return;
    navigator.clipboard.writeText(result).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    });
  };

  // Path'i kopyala
  const handleCopyPath = () => {
    if (!jsonPath) return;
    navigator.clipboard.writeText(jsonPath).then(() => {
      setCopyPathSuccess(true);
      setTimeout(() => setCopyPathSuccess(false), 1200);
    });
  };

  // Satır numaraları (BeautifierPage ile aynı)
  const renderLineNumbers = (text, lineNumbersRefParam) => {
    if (!text) return null;
    const lines = text.split('\n');
    const totalLines = lines.length;
    const lineNumWidth = totalLines > 999 ? 45 : totalLines > 99 ? 35 : 30;
    return (
      <div
        ref={lineNumbersRefParam}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          textAlign: 'right',
          paddingRight: '10px',
          paddingTop: '10px',
          color: '#6e7681',
          userSelect: 'none',
          height: '100%',
          width: `${lineNumWidth}px`,
          fontFamily: '"Consolas", "Monaco", monospace',
          fontSize: '13px',
          fontWeight: '400',
          lineHeight: '1.5',
          zIndex: 1,
          borderRight: '1px solid #e1e4e8',
          backgroundColor: '#f6f8fa',
          overflow: 'hidden'
        }}
      >
        <div style={{ paddingBottom: '20px' }}>
          {lines.map((_, i) => (
            <div key={i} style={{
              lineHeight: '1.5',
              height: '21px',
              paddingRight: '8px',
              color: (i + 1) % 5 === 0 ? '#24292e' : '#6e7681',
              fontWeight: (i + 1) % 10 === 0 ? '600' : '400',
            }}>
              {i + 1}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Ortak stil
  const editorStyle = {
    width: '100%',
    height: '100%',
    padding: '10px 10px 10px 45px',
    border: '1px solid #d0d7de',
    borderRadius: '4px',
    fontFamily: '"Consolas", "Monaco", monospace',
    fontSize: '14px',
    lineHeight: '1.5',
    boxSizing: 'border-box',
    position: 'absolute',
    top: 0,
    left: 0,
    overflowY: 'auto',
    overflowX: 'auto',
    color: '#24292e',
    boxShadow: 'inset 0 1px 2px rgba(27, 31, 35, 0.075)',
  };
  const controlButtonStyle = {
    width: '100%',
    padding: '10px',
    margin: '5px 0',
    backgroundColor: '#00BFA5',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  };

  return (
    <>
      <SEO
        title="JSON Path Finder - Find value by path in JSON"
        description="Find and extract values from JSON data using a path expression. Supports dot notation and array index."
        keywords={["JSON path", "find in JSON", "extract JSON value", "dot notation"]}
        canonicalUrl="https://yourjsontools.com/json-path-finder"
      />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 20px 0' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>JSON Path Finder</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Find and extract values from JSON data using path expressions. Supports dot notation and array indexes.
        </p>
      </div>
      
      <div
        className="jsonpathfinder-container responsive-main-container"
        style={{
          display: 'flex',
          height: 'calc(100vh - 150px)',
          maxHeight: '800px',
          minHeight: '500px',
          margin: '0 auto',
          padding: '20px',
          gap: '40px',
          boxSizing: 'border-box',
        }}
      >
        {/* Sol panel - JSON input */}
        <div className="responsive-panel" style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', minWidth: 0, maxHeight: '100%', overflow: 'hidden' }}>
          <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {renderLineNumbers(jsonInput, inputLineNumbersRef)}
            <TextareaWithLines
              value={jsonInput}
              onChange={e => setJsonInput(e.target.value)}
              placeholder={"Enter your JSON data here..."}
              textareaRef={inputRef}
              lineNumbersRef={inputLineNumbersRef}
              onScroll={handleInputScroll}
              style={{ resize: 'none', overflowY: 'auto' }}
              textareaProps={{ className: 'responsive-textarea' }}
              lineNumbersRefClassName="responsive-linenumbers"
            />
          </div>
        </div>
        {/* Sağ panel - Path ve Sonuç */}
        <div className="responsive-panel" style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', minWidth: 0, maxHeight: '100%', overflow: 'hidden' }}>
          <div style={{ marginBottom: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              value={jsonPath}
              onChange={e => setJsonPath(e.target.value)}
              placeholder="Path (e.g. person.name or people[0].email)"
              style={{ 
                flex: 1, 
                padding: '10px', 
                border: '1px solid #ccc', 
                borderRadius: '4px', 
                fontSize: '14px',
                height: '42px',
                boxSizing: 'border-box'
              }}
              onKeyDown={e => { if (e.key === 'Enter') handleFindPath(); }}
            />
            <button
              onClick={handleCopyPath}
              style={{
                padding: '10px 16px',
                height: '42px',
                border: '1px solid #00BFA5',
                background: copyPathSuccess ? '#4CAF50' : '#fff',
                color: copyPathSuccess ? '#fff' : '#00BFA5',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: 500,
                transition: 'background 0.2s, color 0.2s',
                minWidth: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Copy path"
            >
              {copyPathSuccess ? '✓' : (
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="5" y="5" width="10" height="12" rx="2" stroke="#00BFA5" strokeWidth="1.5" fill="none"/>
                  <rect x="7.5" y="3" width="7.5" height="3.5" rx="1" stroke="#00BFA5" strokeWidth="1.2" fill="none"/>
                </svg>
              )}
            </button>
            <button 
              onClick={handleFindPath} 
              style={{
                ...controlButtonStyle,
                flex: '0 0 100px',
                height: '42px',
                padding: '10px',
                boxSizing: 'border-box'
              }}
            >
              Find
            </button>
          </div>
          <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {renderLineNumbers(result, outputLineNumbersRef)}
            <PreWithClickableLines
              value={result}
              onLineClick={handleResultLineClick}
              preRef={outputRef}
              lineNumbersRef={outputLineNumbersRef}
              onScroll={handleOutputScroll}
              lineNumbersRefClassName="responsive-linenumbers"
              style={{
                width: '100%',
                height: '100%',
                border: '1px solid #d0d7de',
                borderRadius: '4px',
                fontFamily: '"Consolas", "Monaco", monospace',
                fontSize: '14px',
                lineHeight: '1.5',
                boxSizing: 'border-box',
                position: 'absolute',
                top: 0,
                left: 0,
                overflowY: 'auto',
                overflowX: 'auto',
                color: '#24292e',
                boxShadow: 'inset 0 1px 2px rgba(27, 31, 35, 0.075)',
                backgroundColor: '#f6f8fa',
                padding: '10px 10px 10px 0',
              }}
              highlightedPath={highlightedPath}
              resultPaths={resultPaths}
            />
          </div>
          <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileUpload}
              accept=".json,.txt,.js"
            />
            <button onClick={handleUploadClick} style={controlButtonStyle}>Upload JSON</button>
          </div>
          {error && <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>}
        </div>
      </div>
      <div style={{ maxWidth: '1200px', margin: '40px auto 20px', padding: '0 20px', fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
        <h2 style={{ fontSize: '22px', marginBottom: '15px', color: '#333' }}>What is JSON Path Finder?</h2>
        <p style={{ marginBottom: '15px' }}>
          <strong>JSON Path Finder</strong> is a powerful, browser-based tool designed to help you instantly locate and extract any value from complex JSON data structures. Whether you are a developer, data analyst, QA engineer, or just someone working with APIs, this tool streamlines the process of navigating deeply nested JSON objects and arrays.
        </p>
        <h3 style={{ fontSize: '18px', margin: '18px 0 10px', color: '#222' }}>How Does It Work?</h3>
        <p style={{ marginBottom: '12px' }}>
          Simply paste or upload your JSON data into the input area. Enter a path expression using dot notation (e.g. <code>user.profile.name</code>) or array indices (e.g. <code>users[2].email</code>) to pinpoint the exact value you need. The tool instantly parses your JSON and highlights the corresponding value, even in deeply nested or large datasets.
        </p>
        <h3 style={{ fontSize: '18px', margin: '18px 0 10px', color: '#222' }}>Key Features & Advantages</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li><strong>Instant Navigation:</strong> Jump to any value in your JSON with a single path expression.</li>
          <li><strong>Interactive Output:</strong> Click on any line in the result to auto-fill the path input and highlight the selection.</li>
          <li><strong>Supports Dot & Bracket Notation:</strong> Easily access both object properties and array elements.</li>
          <li><strong>Large JSON Support:</strong> Handles large and deeply nested JSON files efficiently, right in your browser.</li>
          <li><strong>Privacy First:</strong> All processing is done locally; your data never leaves your device.</li>
          <li><strong>Copy Path:</strong> Instantly copy the path to any value for use in your code or documentation.</li>
          <li><strong>File Upload:</strong> Quickly load JSON from files for rapid inspection and navigation.</li>
        </ul>
        <h3 style={{ fontSize: '18px', margin: '18px 0 10px', color: '#222' }}>Practical Use Cases</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li><strong>API Response Debugging:</strong> Instantly find and verify values in complex API responses.</li>
          <li><strong>Frontend/Backend Development:</strong> Quickly locate and extract data for UI rendering or backend processing.</li>
          <li><strong>QA & Testing:</strong> Validate specific fields in large JSON payloads during automated or manual testing.</li>
          <li><strong>Data Analysis:</strong> Extract and analyze nested data points from exported JSON datasets.</li>
          <li><strong>Documentation:</strong> Generate precise JSON paths for technical documentation or team communication.</li>
        </ul>
        <h3 style={{ fontSize: '18px', margin: '18px 0 10px', color: '#222' }}>Tips & Best Practices</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li>Use <code>.</code> to access object properties (e.g. <code>order.items[0].price</code>).</li>
          <li>Use <code>[index]</code> to access array elements (e.g. <code>users[3].name</code>).</li>
          <li>Click any result line to auto-fill the path and highlight the value for easy exploration.</li>
          <li>Upload large JSON files for instant navigation without performance loss.</li>
          <li>Copy the path to any value with a single click for use in your codebase.</li>
        </ul>
        <h3 style={{ fontSize: '18px', margin: '18px 0 10px', color: '#222' }}>Why Choose This Tool?</h3>
        <p style={{ marginBottom: '15px' }}>
          Unlike many online JSON viewers, <strong>JSON Path Finder</strong> is optimized for speed, privacy, and developer productivity. It is completely free, requires no registration, and works entirely in your browser—making it the perfect companion for anyone working with JSON data.
        </p>
        <h3 style={{ fontSize: '18px', margin: '18px 0 10px', color: '#222' }}>Example</h3>
        <pre style={{ background: '#f6f8fa', border: '1px solid #e1e4e8', borderRadius: '4px', padding: '12px', fontSize: '14px', marginBottom: '15px', overflowX: 'auto' }}>
          {`{
  "users": [
    { "id": 1, "name": "Alice", "email": "alice@example.com" },
    { "id": 2, "name": "Bob", "email": "bob@example.com" }
  ],
  "meta": { "count": 2 }
}`}
        </pre>
        <p style={{ marginBottom: '15px' }}>
          To get Bob's email, use the path <code>users[1].email</code>. The tool will instantly highlight and extract <code>"bob@example.com"</code> from your JSON.
        </p>
      </div>
    </>
  );
};

export default JsonPathFinderPage; 